/**
 * The messenger's router, on top of Next's. Every messenger screen is a real Next page now
 * (pages/_/chats, pages/_/chats/[id], pages/_/stories/…); this file keeps the small set of
 * react-router hooks the messenger code was written against, so its screens did not have to change.
 *
 * Paths given to these hooks and returned by them stay the messenger's own («/chats/12»);
 * the browser URL is the same path under the site's /_ mount («/_/chats/12/»).
 */
import { forwardRef, useCallback, useEffect, useMemo } from "react";
import NextLink from "next/link";
import Router, { useRouter } from "next/router";
import { fromBrowserPath, toBrowserPath } from "./utils/hamiPaths";

// Old messenger paths that now live elsewhere (they were <Navigate> routes before).
const ALIASES = [
  [/^\/?$/, () => "/chats"],
  [/^\/tickets\/?$/, () => "/chats"],
  [/^\/tickets\/([^/]+)\/?$/, (m) => `/chats/${m[1]}`],
  [/^\/settings\/(.+)$/, (m) => `/chats/settings/${m[1]}`],
];

function resolveAlias(pathname) {
  for (const [re, to] of ALIASES) {
    const m = pathname.match(re);
    if (m) return to(m);
  }
  return pathname;
}

function splitPath(path) {
  const hashAt = path.indexOf("#");
  const hash = hashAt >= 0 ? path.slice(hashAt) : "";
  const rest = hashAt >= 0 ? path.slice(0, hashAt) : path;
  const queryAt = rest.indexOf("?");
  return {
    pathname: queryAt >= 0 ? rest.slice(0, queryAt) : rest,
    search: queryAt >= 0 ? rest.slice(queryAt) : "",
    hash,
  };
}

function currentMessengerLocation() {
  const asPath = Router.asPath || (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/");
  const { pathname, search, hash } = splitPath(asPath);
  const own = fromBrowserPath(pathname).replace(/\/+$/, "") || "/";
  return { pathname: own, search, hash };
}

/** «/chats/12?m=4», { pathname, search } or «?m=4» (same page) → the browser URL */
function toHref(to) {
  const here = currentMessengerLocation();
  let parts;
  if (typeof to === "string") {
    parts = splitPath(to);
    if (!parts.pathname) parts.pathname = here.pathname;
  } else {
    parts = { pathname: to?.pathname ?? here.pathname, search: to?.search ?? "", hash: to?.hash ?? "" };
  }
  if (parts.search && !parts.search.startsWith("?")) parts.search = `?${parts.search}`;
  if (parts.hash && !parts.hash.startsWith("#")) parts.hash = `#${parts.hash}`;
  const pathname = resolveAlias(parts.pathname);
  // the site runs with trailingSlash: true
  const browserPath = toBrowserPath(pathname).replace(/\/?$/, "/");
  return `${browserPath}${parts.search}${parts.hash}`;
}

// location.state of react-router: kept per URL for the page it was handed to
const stateByHref = new Map();
let navigationType = "POP";
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    navigationType = "POP";
  });
}

function normalizedAsPath(asPath) {
  const { pathname, search, hash } = splitPath(asPath);
  return `${pathname.replace(/\/?$/, "/")}${search}${hash}`;
}

// Which Next page a browser path belongs to: the list, chats and chat settings are one page.
function pageOf(browserPathname) {
  return /^\/_\/chats(\/|$)/.test(browserPathname) ? "/_/chats" : browserPathname.replace(/\/?$/, "/");
}

/** Same Next page: a shallow route change — no data request, nothing remounted. */
function isSamePage(href) {
  return pageOf(splitPath(href).pathname) === pageOf(splitPath(Router.asPath || "/").pathname);
}

export function navigateTo(to, options = {}) {
  if (typeof to === "number") {
    navigationType = "POP";
    window.history.go(to);
    return;
  }
  const href = toHref(to);
  if (options.state !== undefined) stateByHref.set(href, options.state);
  else stateByHref.delete(href);
  navigationType = options.replace ? "REPLACE" : "PUSH";
  const go = options.replace ? Router.replace : Router.push;
  go.call(Router, href, undefined, { scroll: false, shallow: isSamePage(href) });
}

export function useNavigate() {
  return useCallback((to, options) => navigateTo(to, options), []);
}

export function useLocation() {
  const router = useRouter();
  const asPath = router.asPath;
  return useMemo(() => {
    const { pathname, search, hash } = splitPath(asPath);
    return {
      pathname: fromBrowserPath(pathname).replace(/\/+$/, "") || "/",
      search,
      hash,
      state: stateByHref.get(normalizedAsPath(asPath)) ?? null,
      key: asPath,
    };
  }, [asPath]);
}

export function useNavigationType() {
  useRouter(); // re-read on every route change
  return navigationType;
}

// The messenger's dynamic paths and the names its screens read them by.
const PARAM_PATTERNS = ["/chats/settings/*", "/chats/:id", "/chat-profile/:remoteId", "/stories/:id"];

/** Dynamic segments of the current messenger path: «/chats/12» → { id: "12" }. */
export function useParams() {
  const { pathname } = useLocation();
  return useMemo(() => {
    for (const pattern of PARAM_PATTERNS) {
      const m = matchPath(pattern, pathname);
      if (m) return pattern === "/chats/settings/*" ? {} : m.params;
    }
    return {};
  }, [pathname]);
}

export function useSearchParams() {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const setSearchParams = useCallback(
    (next, options) => {
      const value = typeof next === "function" ? next(new URLSearchParams(location.search)) : next;
      const search = new URLSearchParams(value).toString();
      navigateTo({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, options);
    },
    [location.pathname, location.search, location.hash]
  );
  return [searchParams, setSearchParams];
}

/** «/chats/:id», «/chats/settings/*» against the current messenger path */
export function matchPath(pattern, pathname) {
  const wildcard = pattern.endsWith("/*");
  const parts = (wildcard ? pattern.slice(0, -2) : pattern).split("/").filter(Boolean);
  const segs = pathname.split("/").filter(Boolean);
  if (wildcard ? segs.length < parts.length : segs.length !== parts.length) return null;
  const params = {};
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].startsWith(":")) params[parts[i].slice(1)] = decodeURIComponent(segs[i]);
    else if (parts[i] !== segs[i]) return null;
  }
  if (wildcard) params["*"] = segs.slice(parts.length).join("/");
  return { params, pathname };
}

export function useMatch(pattern) {
  const { pathname } = useLocation();
  return useMemo(() => matchPath(pattern, pathname), [pattern, pathname]);
}

export const Link = forwardRef(function Link({ to, replace, state, onClick, children, ...rest }, ref) {
  const href = toHref(to);
  return (
    <NextLink
      ref={ref}
      href={href}
      replace={replace}
      scroll={false}
      shallow={isSamePage(href)}
      prefetch={false}
      onClick={(e) => {
        onClick?.(e);
        if (state !== undefined) stateByHref.set(href, state);
        navigationType = replace ? "REPLACE" : "PUSH";
      }}
      {...rest}
    >
      {children}
    </NextLink>
  );
});

export function Navigate({ to, replace, state }) {
  useEffect(() => {
    navigateTo(to, { replace, state });
  }, [to, replace, state]);
  return null;
}
